import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "@/lib/config";
import { getHistory, getSaved, getFollows, replaceLibrary, readRaw, writeRaw, TOKEN_KEY } from "@/lib/storage";

const UserContext = createContext();


/**
 * Optional viewer accounts.
 *
 * The library is local-first: history, saved videos and follows all work with no
 * account at all, exactly as before. An account only adds sync, so the rules here
 * are deliberately one-directional:
 *
 *   - signing in uploads whatever is on this device and merges it server-side,
 *     then writes the merged result back to localStorage;
 *   - localStorage stays the source the UI reads from, so every page keeps working
 *     offline and signed out;
 *   - signing out leaves the local copy alone rather than wiping the device.
 */
export const UserProvider = ({ children }) => {
  const [token, setToken] = useState(() => readRaw(TOKEN_KEY));
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(Boolean(readRaw(TOKEN_KEY)));
  const [syncing, setSyncing] = useState(false);

  const authHeaders = useMemo(
    () => (token ? { Authorization: `Bearer ${token}` } : undefined),
    [token]
  );

  const persistToken = useCallback((next) => {
    writeRaw(TOKEN_KEY, next ?? null);
    setToken(next);
  }, []);

  // Verify a stored token once on mount. An expired one is dropped silently -
  // the visitor simply carries on as a signed-out local user.
  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    axios
      .get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        if (!cancelled) setUser(res.data);
      })
      .catch(() => {
        if (!cancelled) {
          writeRaw(TOKEN_KEY, null);
          setToken(null);
          setUser(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  /** Push this device's library up, pull the merged result back down. */
  const syncLibrary = useCallback(async (activeToken) => {
    const headers = { Authorization: `Bearer ${activeToken}` };
    setSyncing(true);
    try {
      const res = await axios.post(
        `${API}/me/library/merge`,
        { saved: getSaved(), history: getHistory(), follows: getFollows() },
        { headers }
      );
      replaceLibrary(res.data);
      return res.data;
    } catch (e) {
      // A failed sync must not block sign-in; the local library is untouched.
      console.error("Library sync failed:", e);
      return null;
    } finally {
      setSyncing(false);
    }
  }, []);

  const register = useCallback(
    async (email, password) => {
      const res = await axios.post(`${API}/auth/register`, { email, password });
      persistToken(res.data.access_token);
      setUser(res.data.user);
      await syncLibrary(res.data.access_token);
      return res.data.user;
    },
    [persistToken, syncLibrary]
  );

  const login = useCallback(
    async (email, password) => {
      const res = await axios.post(`${API}/auth/login`, { email, password });
      persistToken(res.data.access_token);
      setUser(res.data.user);
      await syncLibrary(res.data.access_token);
      return res.data.user;
    },
    [persistToken, syncLibrary]
  );

  const logout = useCallback(() => {
    // Local library survives on purpose: signing out of a shared device should not
    // silently delete what someone saved before they ever made an account.
    persistToken(null);
    setUser(null);
  }, [persistToken]);

  const deleteAccount = useCallback(async () => {
    await axios.delete(`${API}/auth/me`, { headers: authHeaders });
    persistToken(null);
    setUser(null);
  }, [authHeaders, persistToken]);

  const value = {
    user,
    token,
    loading,
    syncing,
    isSignedIn: Boolean(user),
    authHeaders,
    register,
    login,
    logout,
    deleteAccount,
    syncLibrary,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUser = () => useContext(UserContext);
